import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { BiMove, BiPlus, BiX } from 'react-icons/bi';
import CheckboxFormulaireEtatEntite from '../../../Common/CheckboxFormulaireEtatEntite';
import { useFormikContext } from 'formik';
import { Entente, ModeleHoraire } from '../../../../../../common/src/entities';
import { HeaderTableau } from '../../../../../../common/src/entities/header-tableau.entity';
import { toISODate } from '../../../../Utils/dateUtils';
import ChampFormulaire from '../../../Common/ChampFormulaire';
import { PlageHoraire } from '../../../../../../common/src/entities/plage-horaire.entity';

interface TableHoraireEntenteProps {
    clientActiviteFournisseurMapHoraire?: Map<string, string>;
}

export default function TableHoraireEntente(props: TableHoraireEntenteProps) {
    const { values, setFieldValue } = useFormikContext<Entente | ModeleHoraire>();

    const isEntente = (values as Entente).activiteEntentes !== undefined;

    const defaultColumns = ['Date', 'Heure de début', 'Heure de fin', 'Description', 'Notes'];

    useEffect(() => {
        if (values.horaire) {
            setFieldValue(
                `horaire.plagesHoraires`,
                values.horaire.plagesHoraires.sort((a, b) => a.ordre - b.ordre),
            );
            values.horaire.tableauPlageHoraire.headers.forEach((header, index) => {
                setFieldValue(
                    `horaire.tableauPlageHoraire.headers[${index}].cellules`,
                    header.cellules.sort((a, b) => a.ordre - b.ordre),
                );
            });
            setFieldValue(
                `horaire.tableauPlageHoraire.headers`,
                values.horaire.tableauPlageHoraire.headers.sort((a, b) => a.ordre - b.ordre),
            );
        }
    }, [values.horaire]);

    const [columns, setColumns] = useState(values.horaire.tableauPlageHoraire.headers ?? []);

    const [forceHeureFin, setForceHeureFin] = useState(false);
    const [afficherDate, setAfficherDate] = useState(false);

    const [newColumnName, setNewColumnName] = useState('');
    const [activeCell, setActiveCell] = useState({ row: 0, col: 0 });
    const cellRefs = useRef({});

    useEffect(() => {
        const { row, col } = activeCell;
        const cellRef = cellRefs.current[`${row}-${col}`];
        if (cellRef) {
            cellRef.focus();
        }
    }, [activeCell]);

    const addRow = () => {
        const nouvellePlageHoraire = new PlageHoraire();
        nouvellePlageHoraire.id = Math.floor(Math.random() * 1000000);
        nouvellePlageHoraire.fk_id_tableau_plage_horaire = values.horaire.tableauPlageHoraire.id;
        nouvellePlageHoraire.ordre = values.horaire.plagesHoraires.length + 1;
        nouvellePlageHoraire.pour_quoi = props.clientActiviteFournisseurMapHoraire
            ? Array.from(props.clientActiviteFournisseurMapHoraire.keys()).join(';')
            : '';

        const updatedPlagesHoraires = [...values.horaire.plagesHoraires, nouvellePlageHoraire];

        const updatedHeaders = values.horaire.tableauPlageHoraire.headers.map((header) => {
            const newCellule = {
                id: Math.floor(Math.random() * 1000000),
                name: '',
                ordre: updatedPlagesHoraires.length,
                fk_id_header_tableau: header.id,
            };
            return {
                ...header,
                cellules: [...header.cellules, newCellule],
            };
        });

        setFieldValue('horaire.plagesHoraires', updatedPlagesHoraires);
        setFieldValue('horaire.tableauPlageHoraire.headers', updatedHeaders);
    };

    const removeRow = (index) => {
        const updatedPlagesHoraires = values.horaire.plagesHoraires.filter((_, i) => i !== index);

        updatedPlagesHoraires.forEach((item, i) => {
            item.ordre = i + 1;
        });

        setFieldValue('horaire.plagesHoraires', updatedPlagesHoraires);

        const updatedHeaders = values.horaire.tableauPlageHoraire.headers.map((header) => {
            const updatedCellules = header.cellules.filter((_, i) => i !== index);

            updatedCellules.forEach((cellule, i) => {
                cellule.ordre = i + 1;
            });

            return {
                ...header,
                cellules: updatedCellules,
            };
        });

        setFieldValue('horaire.tableauPlageHoraire.headers', updatedHeaders);
    };

    const addColumn = () => {
        if (newColumnName.trim()) {
            const randomHeaderId = Math.floor(Math.random() * 1000000);
            const newHeader = {
                id: randomHeaderId,
                name: newColumnName,
                ordre: values.horaire.tableauPlageHoraire.headers.length + 1,
                fk_id_tableau_plage_horaire: values.horaire.tableauPlageHoraire.id,
                cellules: values.horaire.plagesHoraires.map((_ph, index) => ({
                    id: Math.floor(Math.random() * 1000000),
                    name: '',
                    ordre: index + 1,
                    fk_id_header_tableau: randomHeaderId,
                })),
            } as HeaderTableau;

            const updatedHeaders = [...values.horaire.tableauPlageHoraire.headers, newHeader];
            setColumns(updatedHeaders);
            setFieldValue('horaire.tableauPlageHoraire.headers', updatedHeaders);
            setNewColumnName('');
        }
    };

    const removeColumn = (index) => {
        const updatedHeaders = values.horaire.tableauPlageHoraire.headers.filter((_, i) => i !== index);

        const updatedColumns = columns.filter((_, i) => i !== index);

        updatedHeaders.forEach((header, i) => {
            header.ordre = i + 1;
        });

        updatedHeaders.forEach((header) => {
            header.cellules.forEach((cellule, i) => {
                cellule.ordre = i + 1;
            });
        });

        setColumns(updatedColumns);
        setFieldValue('horaire.tableauPlageHoraire.headers', updatedHeaders);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = [...values.horaire.plagesHoraires];
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        items.forEach((item, index) => {
            item.ordre = index + 1;
        });

        values.horaire.tableauPlageHoraire.headers.forEach((header, index) => {
            const cells = [...header.cellules];
            const [reorderedCell] = cells.splice(result.source.index, 1);
            cells.splice(result.destination.index, 0, reorderedCell);

            cells.forEach((item, index) => {
                item.ordre = index + 1;
            });
            setFieldValue(`horaire.tableauPlageHoraire.headers[${index}].cellules`, cells);
        });
        setFieldValue('horaire.plagesHoraires', items);
    };

    const handleOnHeureFinChange = (newValue, rowIndex) => {
        let nextRow = values.horaire.plagesHoraires[rowIndex + 1];
        if (nextRow && (!nextRow.debut || nextRow.debut === '' || forceHeureFin)) {
            setFieldValue(`horaire.plagesHoraires[${rowIndex + 1}].debut`, newValue);
        }
    };

    const handleVisibilityChange = (isChecked: boolean, key: string, rowIndex: number) => {
        const currentPourQuoi = values.horaire.plagesHoraires[rowIndex].pour_quoi;
        setFieldValue(
            `horaire.plagesHoraires[${rowIndex}].pour_quoi`,
            isChecked
                ? `${currentPourQuoi};${key}`
                : currentPourQuoi
                      .split(';')
                      .filter((item) => item !== key)
                      .join(';'),
        );
    };

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <InputGroup size="sm" className="w-25">
                        <Form.Control
                            type="text"
                            value={newColumnName}
                            onChange={(e) => setNewColumnName(e.target.value)}
                            placeholder="Nom de la nouvelle colonne"
                        />
                        <InputGroup.Append>
                            <Button
                                type="button"
                                onClick={addColumn}
                                className="rounded-right d-flex align-items-center justify-content-center"
                            >
                                <BiPlus size="1em" />
                            </Button>
                        </InputGroup.Append>
                    </InputGroup>
                    <InputGroup size="sm" style={{ width: 'max-content' }}>
                        <Form.Check
                            type="switch"
                            id="forceHeureFin"
                            checked={forceHeureFin}
                            onChange={() => setForceHeureFin(!forceHeureFin)}
                            label="Forcer l'heure de fin"
                        />
                    </InputGroup>
                    <InputGroup size="sm" style={{ width: 'max-content' }}>
                        <Form.Check
                            type="switch"
                            id="afficherDate"
                            checked={afficherDate}
                            onChange={() => setAfficherDate(!afficherDate)}
                            label="Afficher la date"
                        />
                    </InputGroup>
                </div>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="table">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                                <div
                                    style={{
                                        overflowX: 'auto',
                                        maxWidth: '100em',
                                        minHeight: '10em',
                                    }}
                                >
                                    <table>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                {defaultColumns.map(
                                                    (column, index) =>
                                                        (column !== 'Date' || (column === 'Date' && afficherDate)) && (
                                                            <th key={'default' + index}>{column}</th>
                                                        ),
                                                )}
                                                {columns.map((column, index) => (
                                                    <th key={index}>{column.name}</th>
                                                ))}
                                                <th></th>
                                                {isEntente && <th className="text-center">Visibilité</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {values.horaire.plagesHoraires.map((row, rowIndex) => (
                                                <Draggable
                                                    key={`row-${row.id}`}
                                                    draggableId={`row-${row.id || rowIndex}`}
                                                    index={rowIndex}
                                                >
                                                    {(provided) => (
                                                        <React.Fragment>
                                                            <tr
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className="align-top"
                                                            >
                                                                <td {...provided.dragHandleProps}>
                                                                    <BiMove />
                                                                </td>
                                                                {afficherDate && (
                                                                    <td className="align-middle">
                                                                        <ChampFormulaire
                                                                            id={`horaire.plagesHoraires[${rowIndex}].date`}
                                                                            className="mb-0"
                                                                            type="date"
                                                                            parser={(date) => toISODate(date as string)}
                                                                            estPetit
                                                                            cacherTitre
                                                                        />
                                                                    </td>
                                                                )}
                                                                <td className="align-middle">
                                                                    <ChampFormulaire
                                                                        id={`horaire.plagesHoraires[${rowIndex}].debut`}
                                                                        className="mb-0"
                                                                        type="time"
                                                                        estPetit
                                                                        cacherTitre
                                                                    />
                                                                </td>
                                                                <td className="align-middle">
                                                                    <ChampFormulaire
                                                                        id={`horaire.plagesHoraires[${rowIndex}].fin`}
                                                                        className="mb-0"
                                                                        type="time"
                                                                        estPetit
                                                                        cacherTitre
                                                                        notifyChange={(newValue) =>
                                                                            handleOnHeureFinChange(newValue, rowIndex)
                                                                        }
                                                                    />
                                                                </td>
                                                                <td className="align-middle">
                                                                    <ChampFormulaire
                                                                        id={`horaire.plagesHoraires[${rowIndex}].description`}
                                                                        className="mb-0"
                                                                        type="text"
                                                                        estPetit
                                                                        cacherTitre
                                                                        style={{
                                                                            minWidth: '10em',
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td className="align-middle">
                                                                    <div
                                                                        className="d-flex align-items-center"
                                                                        style={{ gap: '2px' }}
                                                                    >
                                                                        <ChampFormulaire
                                                                            id={`horaire.plagesHoraires[${rowIndex}].notes`}
                                                                            className="flex-grow-1 mb-0"
                                                                            type="text"
                                                                            estPetit
                                                                            cacherTitre
                                                                            style={{ minWidth: '10em' }}
                                                                        />
                                                                        <Button
                                                                            onClick={() => removeRow(rowIndex)}
                                                                            variant="danger"
                                                                            size="sm"
                                                                        >
                                                                            <BiX />
                                                                        </Button>
                                                                    </div>
                                                                </td>
                                                                {values.horaire.tableauPlageHoraire.headers.map(
                                                                    (_, headerIndex) => {
                                                                        return (
                                                                            <td
                                                                                key={headerIndex}
                                                                                className="align-middle"
                                                                            >
                                                                                <ChampFormulaire
                                                                                    id={`horaire.tableauPlageHoraire.headers[${headerIndex}].cellules[${rowIndex}].name`}
                                                                                    className="mb-0"
                                                                                    type="text"
                                                                                    estPetit
                                                                                    cacherTitre
                                                                                    style={{ minWidth: '10em' }}
                                                                                />
                                                                            </td>
                                                                        );
                                                                    },
                                                                )}
                                                                <td className="align-middle"></td>
                                                                {isEntente && (
                                                                    <td
                                                                        colSpan={columns.length + 2}
                                                                        className="mt-1"
                                                                        style={{ display: 'flex' }}
                                                                    >
                                                                        {Array.from(
                                                                            props.clientActiviteFournisseurMapHoraire
                                                                                ? Array.from(
                                                                                      props.clientActiviteFournisseurMapHoraire.entries(),
                                                                                  )
                                                                                : [],
                                                                        ).map(([key, value]) => (
                                                                            <CheckboxFormulaireEtatEntite
                                                                                key={key}
                                                                                id={`horaire.plagesHoraires[${rowIndex}].pour_quoi`}
                                                                                titre={value}
                                                                                isChecked={values.horaire.plagesHoraires[
                                                                                    rowIndex
                                                                                ].pour_quoi.includes(key)}
                                                                                hideFormGroup
                                                                                notifyChange={(isChecked) => {
                                                                                    handleVisibilityChange(
                                                                                        isChecked,
                                                                                        key,
                                                                                        rowIndex,
                                                                                    );
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        </React.Fragment>
                                                    )}
                                                </Draggable>
                                            ))}
                                            <tr>
                                                <td></td>
                                                {defaultColumns.map(
                                                    (column, index) =>
                                                        (column !== 'Date' || (column === 'Date' && afficherDate)) && (
                                                            <td key={index}></td>
                                                        ),
                                                )}
                                                {columns.map((_, index) => (
                                                    <td key={index} className="text-center">
                                                        <Button
                                                            onClick={() => removeColumn(index)}
                                                            variant="danger"
                                                            size="sm"
                                                            className="mb-3"
                                                        >
                                                            <BiX />
                                                        </Button>
                                                    </td>
                                                ))}
                                                <td></td>
                                                <td></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            <Button type="button" onClick={addRow} className="d-flex align-items-center justify-content-center">
                <BiPlus size="1em" />
            </Button>
        </div>
    );
}
